import { queryOptions } from '@tanstack/react-query'
import { listNotificationsOptions, STALE } from '@/shared/api'

/**
 * Лента уведомлений (`GET /notifications`). На каждом экране НЕ висит: для точки на колокольчике
 * есть нарочно дешёвая `badgesQuery` (`entities/badge`). Лента грузится, только пока открыт
 * поповер — `enabled` стоит на вызывающей стороне (`features/notifications-read`, она и знает,
 * открыт ли он).
 *
 * `staleTime: STALE.live` (0) — это и есть механизм обновления. `enabled` переключается в `true`
 * при каждом открытии, а с нулевым staleTime закэшированные данные в этот момент считаются
 * устаревшими и перезапрашиваются. Без него повторное открытие в пределах дефолтных 30 секунд
 * показало бы старый снимок — уже помеченный прочитанным на сервере, но не на клиенте.
 *
 * ⚠️ ИНВАРИАНТ: ПОКА ПОПОВЕР ОТКРЫТ, ЛЕНТА НЕ ПЕРЕЗАПРАШИВАЕТСЯ. НИКАК. НИЧЕМ.
 *
 * Открытие поповера помечает уведомления прочитанными (`markNotificationsRead`, так велит
 * контракт). Значит любой ПОВТОРНЫЙ запрос той же ленты вернёт `unreadCount: 0` и `read: true`
 * у всех элементов — счётчик «N новых» и подсветка непрочитанных погаснут **у человека под
 * курсором, пока он читает**.
 *
 * ⚠️ СЕЙЧАС вызов мутации снят: сервиса уведомлений нет, лента приходит пустой, а сама операция
 * отвечает 501 (см. `header.notifications` в shared/config/roadmap.ts). Глушители ниже остаются
 * на месте намеренно — восстанавливать инвариант вместе с сервисом, по четырём дверям сразу,
 * пришлось бы заново, и одну из них снова забыли бы.
 *
 * К этому исходу вели ЧЕТЫРЕ разные двери. Я закрывал их по одной, каждый раз считая вопрос
 * решённым, — и каждый раз находилась следующая:
 *
 *   1. `invalidateQueries` после мутации — не заводили;
 *   2. `refetchInterval` — стоял, убран. Живой прогон: поповер открыт → 1 запрос; через 33 с,
 *      поповер всё ещё открыт → 2 запроса, и шапка превращалась из «Уведомления · 3 новых»
 *      в просто «Уведомления»;
 *   3. `refetchOnWindowFocus` (дефолт `true`) — любой возврат во вкладку сбрасывал список:
 *      открыл уведомления, переключился на почту, вернулся — счётчик погас;
 *   4. `refetchOnReconnect` (дефолт `true`) — то же самое при любом моргании сети. Ирония:
 *      восстановление связи дёргает тот самый `onlineManager`, на который подписан
 *      `OfflineBanner`. Баннер «данные обновятся, когда сеть вернётся» буквально анонсировал
 *      этот баг.
 *
 * Корень у всех четырёх один: `staleTime: 0` делает ленту ВСЕГДА протухшей, а значит любой
 * автоматический триггер обновления по ней срабатывает. Убрать ноль нельзя — на нём и держится
 * свежесть при открытии (`enabled` переключается в `true`, данные считаются устаревшими,
 * запрос уходит). Поэтому триггеры глушатся поимённо.
 *
 * **Список триггеров закрыт тестом** (`queries.test.ts`): он падает, если кто-то добавит пятую
 * дверь или снимет глушитель. Полагаться на то, что следующий человек прочтёт этот комментарий,
 * я больше не готов — трижды не прочёл я сам.
 *
 * Мораль, которая дороже самого бага: требование звучало «не инвалидируй ленту». Первая дверь
 * была закрыта буквально, а исход достигался тремя другими путями, ничего не нарушая.
 * **Запрещать надо ИСХОД, а не механизм.**
 *
 * Опрашивать список, на который человек прямо сейчас смотрит, не нужно вовсе: точку на
 * колокольчике обновляет `badgesQuery`.
 */
export function notificationsQuery() {
  return queryOptions({
    ...listNotificationsOptions(),

    // Свежесть при каждом открытии поповера.
    staleTime: STALE.live,

    // …и ни одного автоматического обновления, кроме него. Не снимать поодиночке.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // `refetchInterval` НЕ ЗАДАВАТЬ — это была вторая дверь.
  })
}
